import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { ProdutoIcmsRow, SalvarProdutoIcmsRequest } from "@/features/produtos/hooks/useProdutoIcms";

const UFS_BR = ["AC","AL","AM","AP","BA","CE","DF","ES","GO","MA","MG","MS","MT","PA","PB","PE","PI","PR","RJ","RN","RO","RR","RS","SC","SE","SP","TO"];
const MOD_BC = [
  { value: "0", label: "0 — Margem Valor Agregado (%)" },
  { value: "1", label: "1 — Pauta (valor)" },
  { value: "2", label: "2 — Preço Tabelado Máx. (valor)" },
  { value: "3", label: "3 — Valor da operação" },
];
const MOD_BC_ST = [
  { value: "0", label: "0 — Preço tabelado/máximo sugerido" },
  { value: "1", label: "1 — Lista Negativa (valor)" },
  { value: "2", label: "2 — Lista Positiva (valor)" },
  { value: "3", label: "3 — Lista Neutra (valor)" },
  { value: "4", label: "4 — Margem Valor Agregado (%)" },
  { value: "5", label: "5 — Pauta (valor)" },
];
const MOTIVOS_DESONERACAO = [
  { value: "__none__", label: "Nenhum" },
  { value: "TAXI", label: "Táxi" }, { value: "DEFICIENTE_CONDUTOR", label: "Deficiente condutor" },
  { value: "DEFICIENTE_NAO_CONDUTOR", label: "Deficiente não condutor" }, { value: "PRODUTO_AGROPECUARIO", label: "Produto agropecuário" },
  { value: "FROTISTA_LOCADORA", label: "Frotista/Locadora" }, { value: "DIPLOMATICO_CONSULAR", label: "Diplomático/Consular" },
  { value: "SUFRAMA", label: "SUFRAMA" }, { value: "VENDA_ORGAOS_PUBLICOS", label: "Venda a Órgãos Públicos" }, { value: "OUTROS", label: "Outros" },
];

const EMPTY: SalvarProdutoIcmsRequest = {
  ufIcmsSt: null, situacaoTributariaId: null, csosnId: null, tributacaoIcms: null,
  modDeterminacaoBC: null, percentualReducaoBC: null, aliquotaIcms: null, percentualBCOperacaoPropria: null,
  aliquotaIcmsAproveitado: null, destacarIcms: false, modDeterminacaoBCSt: null, percentualReducaoBCSt: null,
  aliquotaIcmsSt: null, margemValorAdicionadoSt: null, precoBaseSubstituicao: null,
  motivoDesoneracao: null, cBnef: null, aliquotaCbs: 0, aliquotaIbsUf: 0, aliquotaIbsMunicipio: 0,
};

interface Props {
  open: boolean;
  row: ProdutoIcmsRow | null;
  cstIcmsCombo: { id: number; cst: string | null; descricao: string | null }[];
  csosnCombo: { id: number; codigoCsosn: number; descricao: string | null }[];
  onClose: () => void;
  onSave: (data: SalvarProdutoIcmsRequest, id?: number) => void;
}

export function ProdutoIcmsDialog({ open, row, cstIcmsCombo, csosnCombo, onClose, onSave }: Props) {
  const [f, setF] = useState<SalvarProdutoIcmsRequest>(EMPTY);
  useEffect(() => { if (open) setF(row ? { ...row, id: undefined } as SalvarProdutoIcmsRequest : EMPTY); }, [open, row]);

  const set = <K extends keyof SalvarProdutoIcmsRequest>(k: K, v: SalvarProdutoIcmsRequest[K]) => setF(p => ({ ...p, [k]: v }));
  const num = (label: string, key: keyof SalvarProdutoIcmsRequest) => (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Input type="number" step="0.01" min="0" className="h-8 text-sm"
        value={(f[key] as number | null) ?? ""} onChange={(e) => set(key, (e.target.value === "" ? null : Number(e.target.value)) as never)} />
    </div>
  );
  const modSelect = (label: string, key: keyof SalvarProdutoIcmsRequest, opts: { value: string; label: string }[]) => (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Select value={(f[key] as number | null) != null ? String(f[key]) : "__none__"} onValueChange={(v) => set(key, (v === "__none__" ? null : parseInt(v)) as never)}>
        <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="—" /></SelectTrigger>
        <SelectContent><SelectItem value="__none__">—</SelectItem>{opts.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{row ? "Editar" : "Novo"} ICMS por UF</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">UF</Label>
            <Select value={f.ufIcmsSt ?? "__none__"} onValueChange={(v) => set("ufIcmsSt", v === "__none__" ? null : v)}>
              <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent><SelectItem value="__none__">—</SelectItem>{UFS_BR.map(uf => <SelectItem key={uf} value={uf}>{uf}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">CST ICMS (Saída)</Label>
            <Select value={f.tributacaoIcms ?? "__none__"} onValueChange={(v) => set("tributacaoIcms", v === "__none__" ? null : v)}>
              <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent><SelectItem value="__none__">—</SelectItem>{cstIcmsCombo.map(c => <SelectItem key={c.id} value={c.cst ?? "?"}>{c.cst} — {c.descricao}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">CSOSN</Label>
            <Select value={f.csosnId != null ? String(f.csosnId) : "__none__"} onValueChange={(v) => set("csosnId", v === "__none__" ? null : parseInt(v))}>
              <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent><SelectItem value="__none__">—</SelectItem>{csosnCombo.map(c => <SelectItem key={c.id} value={String(c.id)}>{c.codigoCsosn} — {c.descricao}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          {modSelect("Mod. Det. BC ICMS", "modDeterminacaoBC", MOD_BC)}
          {num("Alíq. ICMS (%)", "aliquotaIcms")}
          {num("Red. BC ICMS (%)", "percentualReducaoBC")}
          {num("BC Oper. Própria (%)", "percentualBCOperacaoPropria")}
          {num("Alíq. ICMS Aproveitado (%)", "aliquotaIcmsAproveitado")}

          {modSelect("Mod. Det. BC ST", "modDeterminacaoBCSt", MOD_BC_ST)}
          {num("Alíq. ICMS ST (%)", "aliquotaIcmsSt")}
          {num("Red. BC ST (%)", "percentualReducaoBCSt")}
          {num("Margem Valor ST (%)", "margemValorAdicionadoSt")}
          {num("Preço Base Substituição", "precoBaseSubstituicao")}

          {num("Alíq. CBS (%)", "aliquotaCbs")}
          {num("Alíq. IBS UF (%)", "aliquotaIbsUf")}
          {num("Alíq. IBS Município (%)", "aliquotaIbsMunicipio")}

          <div className="space-y-1">
            <Label className="text-xs">Motivo Desoneração</Label>
            <Select value={f.motivoDesoneracao || "__none__"} onValueChange={(v) => set("motivoDesoneracao", v === "__none__" ? null : v)}>
              <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Nenhum" /></SelectTrigger>
              <SelectContent>{MOTIVOS_DESONERACAO.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">CBenef</Label>
            <Input className="h-8 text-sm" value={f.cBnef ?? ""} onChange={(e) => set("cBnef", e.target.value || null)} />
          </div>
        </div>
        <div className="flex items-center gap-2 mt-1">
          <Switch checked={f.destacarIcms} onCheckedChange={(v) => set("destacarIcms", v)} />
          <Label className="text-sm">Destaca ICMS ST na NF-e</Label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => onSave(f, row?.id)} disabled={!f.ufIcmsSt}>Salvar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
